import { useState } from 'preact/hooks'
import type { ComponentChildren } from 'preact'
import { Search, Loader2, Check, AlertTriangle, Users } from 'lucide-preact'
import { userRolesApi } from '../../api/userRoles'
import type { ManagingCompanyUserDto } from '../../api/userRoles'
import { PersonAvatar } from '../common/Avatar'
import { BRAND_CLASSES } from '@/constants/theme'
import type { RoleType, UserProfileWithRolesDto } from '../../types/userRoles'
import { getDisplayErrorMessages } from '../../utils/errorHandling'
import { useToast } from '../../hooks/useToast'

const ROLES: RoleType[] = ['Viewer', 'Editor', 'Admin']

/** What became of one person after Add. A failed person stays ticked, so Add tries them again. */
type RowResult = { added: true; role: RoleType } | { added: false; error: string }

interface ManagingCompanyPickerProps {
  /** The company people are being added to. */
  companyId: string
  companyName: string
  managingCompanyName: string
  people: ManagingCompanyUserDto[]
  loading: boolean
  /** The two route cards, rendered above the search so they scroll with the list. */
  modeSwitcher: ComponentChildren
  onClose: () => void
  /** Everyone the last Add gave access to. The modal stays open so the rows can report each result. */
  onAdded: (users: UserProfileWithRolesDto[]) => void
}

/**
 * ORB-1019: tick several of the managing company's people, give each a role, and add them in one
 * request. "Add as" fills the role on every ticked row; each row can still be changed on its own.
 * Choosing the role is part of this step, so this route has no separate Roles step.
 *
 * Company roles only. Somebody who needs workspace roles alone is given them afterwards with Edit roles.
 */
export function ManagingCompanyPicker({
  companyId,
  companyName,
  managingCompanyName,
  people,
  loading,
  modeSwitcher,
  onClose,
  onAdded,
}: ManagingCompanyPickerProps) {
  const { showSuccess, showWarning, showErrorUnless422 } = useToast()
  const [search, setSearch] = useState('')
  const [addAs, setAddAs] = useState<RoleType>('Editor')
  const [selected, setSelected] = useState<Map<string, RoleType>>(new Map())
  const [results, setResults] = useState<Map<string, RowResult>>(new Map())
  const [summary, setSummary] = useState<{ added: number; failed: number } | null>(null)
  const [requestError, setRequestError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  /** Rows sent in the request still in flight. Locked until it answers, so each outcome lands on its row. */
  const [inFlight, setInFlight] = useState<Set<string>>(new Set())

  const query = search.trim().toLowerCase()
  const matching = people.filter(person =>
    !query
    || `${person.FirstName} ${person.LastName}`.toLowerCase().includes(query)
    || person.Email.toLowerCase().includes(query))

  const isAdded = (id: string) => results.get(id)?.added === true
  const pending = [...selected.entries()].filter(([id]) => !isAdded(id))
  const anyAdded = [...results.values()].some(result => result.added)
  // "Add as" describes every ticked row, including any the search has hidden, because those are what it
  // rewrites and what Add sends. Ticked rows that all agree show their role; rows that differ read Custom.
  const pendingRoles = new Set(pending.map(([, role]) => role))
  const shownAddAs: RoleType | 'Custom' = pendingRoles.size === 0 ? addAs
    : pendingRoles.size === 1 ? pending[0][1] : 'Custom'
  // A newly ticked row takes the role on screen; under Custom, the last one chosen in "Add as".
  const roleForNewTick = shownAddAs === 'Custom' ? addAs : shownAddAs

  const toggle = (person: ManagingCompanyUserDto, checked: boolean) => {
    // Guarded as well as disabled: an ineligible row must not depend on the attribute being honoured.
    if (!person.CanBeAdded || isAdded(person.UserProfileId) || inFlight.has(person.UserProfileId)) return
    setSelected(previous => {
      const next = new Map(previous)
      if (checked) next.set(person.UserProfileId, roleForNewTick)
      else next.delete(person.UserProfileId)
      return next
    })
    setResults(previous => {
      const next = new Map(previous)
      next.delete(person.UserProfileId)
      return next
    })
  }

  const setRowRole = (id: string, role: RoleType) =>
    setSelected(previous => new Map(previous).set(id, role))

  const applyAddAs = (role: RoleType) => {
    setAddAs(role)
    setSelected(previous => new Map([...previous.keys()].map(id => [id, role])))
  }

  const handleAdd = async () => {
    if (pending.length === 0) return
    setSubmitting(true)
    setRequestError(null)
    setInFlight(new Set(pending.map(([id]) => id)))

    const response = await userRolesApi.grantManagingCompanyAccessBatch(
      companyId,
      pending.map(([id, role]) => ({ UserProfileId: id, InitialRoles: { CompanyRoles: [role], WorkspaceRoles: [] } })),
    )
    setSubmitting(false)
    setInFlight(new Set())

    if (response.error || !response.data) {
      const message = getDisplayErrorMessages(response.error ?? '', 'Nobody was added. Try again.').join(' ')
      setRequestError(message)
      showErrorUnless422('Nobody was added', { status: response.status, error: message })
      return
    }

    const roleFor = new Map(pending)
    const outcomes = response.data.Results
    const addedUsers = outcomes.flatMap(outcome => outcome.Succeeded && outcome.UserProfileWithRoles ? [outcome.UserProfileWithRoles] : [])

    // Merged into the latest state rather than the snapshot taken at click time: a row ticked, or a
    // role changed, while the request was in flight must survive the response.
    setResults(previous => {
      const next = new Map(previous)
      for (const outcome of outcomes) {
        next.set(outcome.UserProfileId, outcome.Succeeded
          ? { added: true, role: roleFor.get(outcome.UserProfileId) ?? addAs }
          : { added: false, error: outcome.Error || 'Could not be added. Try again.' })
      }
      return next
    })
    setSelected(previous => {
      const next = new Map(previous)
      for (const outcome of outcomes) {
        if (outcome.Succeeded) next.delete(outcome.UserProfileId)
      }
      return next
    })
    const added = outcomes.filter(outcome => outcome.Succeeded).length
    const failed = outcomes.length - added
    setSummary({ added, failed })
    // The summary under the list says the same, but can sit below the fold of a long list, so every
    // outcome is also a toast.
    if (failed === 0) {
      showSuccess('People added', `Added ${added} ${added === 1 ? 'person' : 'people'} to ${companyName}.`)
    } else if (added > 0) {
      showWarning('Some people not added', `Added ${added} of ${outcomes.length}. The rest are still selected, so Add tries again.`)
    } else {
      showWarning('Nobody was added', `${failed === 1 ? 'They are' : `All ${failed} are`} still selected, so Add tries again.`)
    }
    if (addedUsers.length > 0) onAdded(addedUsers)
  }

  const addLabel = pending.length === 0
    ? 'Add people'
    : pending.length === 1 ? 'Add 1 person' : `Add ${pending.length} people`

  return (
    <>
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 py-6 space-y-4">
          {modeSwitcher}

          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="relative w-full max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
              <input
                type="search"
                value={search}
                onInput={(e) => setSearch((e.target as HTMLInputElement).value)}
                aria-label="Search people"
                className="w-full pl-9 pr-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-slate-100"
                placeholder="Search people"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300">
              Add as
              <select
                value={shownAddAs}
                // Locked with the rows: rewriting the roles of rows in flight would retry a refused row
                // with a role it was never sent with.
                disabled={submitting}
                onChange={(e) => applyAddAs((e.target as HTMLSelectElement).value as RoleType)}
                className="px-2.5 py-1.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                {shownAddAs === 'Custom' && <option value="Custom" disabled>Custom</option>}
                {ROLES.map(role => <option key={role} value={role}>{role}</option>)}
              </select>
            </label>
          </div>

          <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-800">
            {loading ? (
              <div className="flex justify-center py-6">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400 dark:text-slate-500" />
              </div>
            ) : matching.length === 0 ? (
              <p className="px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
                Nobody at {managingCompanyName} matches that.
              </p>
            ) : (
              matching.map(person => {
                const id = person.UserProfileId
                const name = `${person.FirstName} ${person.LastName}`
                const result = results.get(id)
                const added = result?.added === true
                const blocked = !person.CanBeAdded || added
                const checked = selected.has(id) && !blocked
                const locked = inFlight.has(id)
                const checkboxId = `pick-${id}`
                const reasonId = `pick-reason-${id}`

                return (
                  <div
                    key={id}
                    className={`flex items-center gap-3 px-4 py-2.5 border-b border-slate-200 dark:border-slate-700 last:border-b-0 transition-colors ${blocked
                      ? 'bg-slate-50 dark:bg-slate-900/40'
                      : checked ? 'bg-primary-50 dark:bg-primary-900/20' : 'hover:bg-slate-50 dark:hover:bg-slate-700'
                      }`}
                  >
                    <input
                      id={checkboxId}
                      type="checkbox"
                      checked={checked}
                      disabled={blocked || locked}
                      aria-label={`Select ${name}`}
                      // The reason a row can't be ticked, or why it failed, is read out with the checkbox.
                      aria-describedby={(!person.CanBeAdded && person.IneligibleReason) || (result && !result.added) ? reasonId : undefined}
                      onChange={(e) => toggle(person, (e.target as HTMLInputElement).checked)}
                      className={`h-4 w-4 rounded border-slate-300 dark:border-slate-600 text-primary-600 dark:text-primary-500 ${BRAND_CLASSES.FOCUS.PRIMARY} disabled:cursor-not-allowed`}
                    />
                    <PersonAvatar firstName={person.FirstName} lastName={person.LastName} profilePicUrl={person.AvatarUrl} size="md" />
                    <label htmlFor={checkboxId} className={`flex-1 min-w-0 ${blocked ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
                      <span className={`block text-sm font-medium truncate ${person.CanBeAdded ? 'text-slate-900 dark:text-white' : 'text-slate-400 dark:text-slate-500'}`}>
                        {name}
                      </span>
                      <span className={`block text-xs truncate ${person.CanBeAdded ? 'text-slate-500 dark:text-slate-400' : 'text-slate-400 dark:text-slate-500'}`}>
                        {person.Email}
                      </span>
                    </label>

                    {/* Server-computed prose, shown as it arrives: the reason is not ours to word. */}
                    {!person.CanBeAdded && person.IneligibleReason && (
                      <span id={reasonId} className="text-xs text-amber-600 dark:text-amber-400 whitespace-nowrap">{person.IneligibleReason}</span>
                    )}
                    {added && (
                      <span className="flex items-center gap-1 text-xs font-medium text-emerald-700 dark:text-emerald-400 whitespace-nowrap">
                        <Check className="w-3.5 h-3.5" />
                        Added as {result.role}
                      </span>
                    )}
                    {checked && (
                      <span className="flex items-center gap-2 min-w-0">
                        {result && !result.added && (
                          <span id={reasonId} className="text-xs text-red-600 dark:text-red-400 text-right">{result.error}</span>
                        )}
                        <select
                          value={selected.get(id)}
                          disabled={locked}
                          aria-label={`Role for ${name}`}
                          onChange={(e) => setRowRole(id, (e.target as HTMLSelectElement).value as RoleType)}
                          className="px-2 py-1 border border-slate-300 dark:border-slate-600 rounded-md bg-white dark:bg-slate-700 text-sm text-slate-900 dark:text-slate-100 focus:outline-none focus:ring-2 focus:ring-primary-500"
                        >
                          {ROLES.map(role => <option key={role} value={role}>{role}</option>)}
                        </select>
                      </span>
                    )}
                  </div>
                )
              })
            )}
          </div>

          <div aria-live="polite">
            {summary && (summary.failed > 0 ? (
              <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-md bg-amber-50 dark:bg-amber-900/20 text-sm text-amber-800 dark:text-amber-300">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Added {summary.added} of {summary.added + summary.failed}.{' '}
                  {summary.failed === 1 ? 'One person' : `${summary.failed} people`} could not be added; they are still
                  selected, so Add tries again.
                </span>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 px-3 py-2.5 rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-sm text-emerald-800 dark:text-emerald-300">
                <Check className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>
                  Added {summary.added} {summary.added === 1 ? 'person' : 'people'} to {companyName}.
                </span>
              </div>
            ))}
            {requestError && (
              <div className="px-3 py-2.5 rounded-md bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
                {requestError}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="px-6 py-4 border-t border-slate-200 dark:border-slate-700">
        <div className="flex items-center justify-end gap-3">
          <span className="mr-auto text-sm tabular-nums text-slate-500 dark:text-slate-400">{pending.length} selected</span>
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {anyAdded ? 'Done' : 'Cancel'}
          </button>
          <button
            onClick={handleAdd}
            disabled={submitting || pending.length === 0}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Users className="w-4 h-4" />}
            <span>{submitting ? 'Adding…' : addLabel}</span>
          </button>
        </div>
      </div>
    </>
  )
}
